import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HeaderLogo,
  ContactUsLink,
  HamburgerMenuLink,
  FooterDisclaimer,
} from 'organisms';
import { Stepper, Link } from '@sentara-health/optimahealth_web';
import RegistrationSteps from './RegistrationSteps';
import { GUEST_REGISTRATION_STEP_ONE, REGISTRATION_STEP_ONE, UserDetails, FormTitles as FormTitleConst, planTypeDropDown } from 'common';
import LanguageDropdown from 'organisms/languageDropdown/LanguageDropdown';
import { GlobalContext, isAvMed } from 'context';

function Form() {
  const {
    getFeature
  } = useContext(GlobalContext);
  document.cookie = UserDetails.optimaPersonCookie + '=';
  document.cookie = UserDetails.optimaRoleCookie + '=';
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const [item] = useState(planTypeDropDown);
  const guest: boolean = !(params.get('guest') === null || params.get('guest') === 'false');
  // AvMed has no Get Started step, so guest registration begins on Personal Information (page 0).
  const switchguest = guest && !isAvMed() ? GUEST_REGISTRATION_STEP_ONE : REGISTRATION_STEP_ONE;
  const { t } = useTranslation();
  const [page, setPage] = useState(switchguest);
  const [plantypes, setPlantypes] = useState<string>('');
  const [formData, setFormData] = useState<any>({});

  const generateStepperLabels = (numSteps: number) => {
    return Array.from({ length: numSteps }, (_, index) => `${t('common.step')} ${index + 1}`);
  };
  const StepperLabels = generateStepperLabels(FormTitleConst.length - 1);
  const FormTitles = FormTitleConst;

  const wordNumbers = ['One', 'Two', 'Three', 'Four', 'Five'];

  const labelsToUseCheck = () => {
    if (guest) {
      // AvMed hides one fewer pre-step (no Get Started) than SHP.
      return StepperLabels.slice(0, isAvMed() ? -1 : -2);
    } else if (getFeature?.MpcInRegistration === false) {
      return StepperLabels.slice(0, -1);
    } else {
      return StepperLabels;
    }
  };

  const indexValueCheck = () => {
    if (guest) {
      return isAvMed() ? 1 : 2;
    } else if (getFeature?.MpcInRegistration === false) {
      return 1;
    } else {
      return 0;
    }
  };

  const generateStepperData = (currentPage: number) => {
    const labelsToUse = labelsToUseCheck();
    const indexValue = indexValueCheck();
    return (labelsToUse ?? [])
      .filter((label) => label)
      .map((label, index) => ({
        label,
        number: index + 1,
        testId: `stepperStep${wordNumbers[index + 1]}`,
        active: currentPage === index + indexValue,
        selected: currentPage >= index + indexValue + 1,
      }));
  };

  const stepperData = generateStepperData(page);

  return (
    <div className="d-flex flex-column h-100">
      {/* Skip to Main Content link */}
      <Link to="#main-content" className="skip-link">
        {t('common.skipToMainContent')}
      </Link>
      {/* Header section */}
      <header className='border-bottom'>
        {/* Container for the header */}
        <div className="container-fluid">
          {/* Navigation bar */}
          <nav className="header container-lg d-flex align-items-center justify-content-between">
            <HeaderLogo />
            <div className="d-flex flex-no-wrap align-items-center">
              {/* Contact us link, visible on extra-large and large screens */}
              <div className="d-none d-xl-block d-lg-block d-md-none">
                <ContactUsLink showIcon={false} />
              </div>
              {/* Language selection dropdown, visible on extra-large and large screens */}
              {getFeature?.MultiLanguage && (
                <div className="d-none d-xl-block d-lg-block d-md-none position-relative px-4">
                  <LanguageDropdown />
                </div>
              )}
              {/* Hamburger menu link, visible on large, medium, and small screens */}
              <div className="d-lg-none d-md-block d-sm-block">
                <HamburgerMenuLink showLogout={false} />
              </div>
            </div>
          </nav>
        </div>
      </header>
      {/* Main content container */}
      <div className="container-fluid" >
        <div
          id="main-content"
          className='container-lg px-main'
        >
          <div className="row">
            <div className="d-flex justify-content-center align-items-center px-md-4">
              <div className="col-lg-11 col-12 py-lg-4 py-2 px-lg-5 px-sm-4 mb-lg-4 mb-2">
                <Stepper registerSteps={stepperData} />
              </div>
            </div>
            <div className="col-12 px-md-5 px-0">
              <RegistrationSteps
                page={page}
                setPage={setPage}
                formData={formData}
                setFormData={setFormData}
                setPlantypes={setPlantypes}
                plantypes={plantypes}
                item={item}
                guest={guest}
                t={t}
                FormTitles={FormTitles}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Footer section */}
      <footer className="container-lg mt-auto">
        <hr className='mt-3 mb-4 pt-2' />
        {/* Component to display the footer disclaimer */}
        <FooterDisclaimer />
      </footer>
    </div>
  );
}

export default Form;